import { useEffect, useState } from "react";
import { Building, Search, MessageCircle } from "lucide-react";
import { portal } from "../lib/api";
import SpotlightTour from "./SpotlightTour";

const STORAGE_KEY = "ikey_portal_tour_seen_v1";
const RESTART_KEY = "ikey_portal_tour_restart";
const CLIENT_KEY = "realtor_os_portal_client";

// Exposed helper — client bubble / portal "Restart tour" link calls this.
export function restartClientTour() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.setItem(RESTART_KEY, String(Date.now()));
  window.dispatchEvent(new CustomEvent("ikey:restart-portal-tour"));
}

const STEPS = [
  {
    icon: Building,
    eyebrow: "Welcome to your portal",
    title: "Everything about your deal, in one place",
    body: "Your agent will share properties, docs, and updates here. No app to install — this page is bookmark-able and syncs across your phone and computer.",
    target: null,
  },
  {
    icon: Building,
    eyebrow: "Step 2 · Properties",
    title: "Recommended listings from your agent",
    body: "Tap the Properties tab to see homes your agent has hand-picked for you. Like the ones you love, request showings, and pass on the rest so your agent knows what to send next.",
    target: '[data-testid="portal-tab-properties"]',
  },
  {
    icon: Search,
    eyebrow: "Step 3 · Search the market",
    title: "Browse the MLS without leaving iKEY",
    body: "Your agent's MLS search is embedded inside the Properties tab so you can browse the whole market and still ping your agent with one tap.",
    target: '[data-testid="portal-tab-properties"]',
  },
  {
    icon: MessageCircle,
    eyebrow: "Step 4 · Message your agent",
    title: "Chat with your agent any time",
    body: "The blue bubble in the bottom-right of every page opens a direct line to your agent. Any new reply pings you with a red dot right there.",
    target: '[data-testid="portal-agent-bubble"]',
  },
];

/**
 * Client-side welcome tour. Renders on the first portal login only — server
 * flag `client.welcome_tour_completed_at` is the source of truth.
 */
export default function ClientWelcomeTour() {
  const [open, setOpen] = useState(false);
  const [client, setClient] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Agent-impersonation mode: never fire the client tour for the agent
      // previewing a portal (they've already got their own tour).
      try { if (localStorage.getItem("realtor_os_impersonate_active") === "1") return; }
      catch (e) { console.debug("impersonate-flag read failed", e); }
      let c = null;
      try {
        const raw = localStorage.getItem(CLIENT_KEY);
        c = raw ? JSON.parse(raw) : null;
      } catch (e) { console.debug("client localStorage parse failed", e); }
      setClient(c);

      // Manual restart wins over everything.
      if (localStorage.getItem(RESTART_KEY)) {
        localStorage.removeItem(RESTART_KEY);
        if (!cancelled) setOpen(true);
        return;
      }
      try {
        const { data } = await portal.get("/portal/me");
        if (cancelled) return;
        const serverClient = data.client || {};
        if (serverClient.welcome_tour_completed_at) {
          localStorage.setItem(STORAGE_KEY, "1");
          try { localStorage.setItem(CLIENT_KEY, JSON.stringify({ ...c, ...serverClient })); }
          catch (e) { /* silent */ }
          return;
        }
      } catch (e) {
        if (localStorage.getItem(STORAGE_KEY)) return;
      }
      if (localStorage.getItem(STORAGE_KEY)) return;
      if (cancelled) return;
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onRestart = () => setOpen(true);
    window.addEventListener("ikey:restart-portal-tour", onRestart);
    return () => window.removeEventListener("ikey:restart-portal-tour", onRestart);
  }, []);

  const markComplete = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    try {
      if (client) {
        const now = new Date().toISOString();
        localStorage.setItem(CLIENT_KEY, JSON.stringify({ ...client, welcome_tour_completed_at: now }));
      }
    } catch (e) { /* silent */ }
    portal.post("/portal/welcome-tour-complete").catch(() => {});
  };

  const handleFinish = () => { markComplete(); setOpen(false); };
  const handleSkip = () => { markComplete(); setOpen(false); };

  if (!open) return null;
  return (
    <SpotlightTour
      steps={STEPS}
      onFinish={handleFinish}
      onSkip={handleSkip}
      brandName="iKEY"
      testIdPrefix="portal-welcome-tour"
    />
  );
}
